import api from "./api";
import * as db from "./offlineDb";

// Offline-aware read/write helpers for Notes captures. Every function tries
// the real network call first; only when that's genuinely unreachable
// (no navigator.onLine, or the request throws with no server response at
// all) does it fall back to the local IndexedDB cache / mutation queue.
// A real server error (4xx/5xx, i.e. `e.response` is set) is NOT treated as
// "offline" and is re-thrown as-is so existing error handling keeps working.
const STORE = "captures";
const CACHE_DAYS = 3;

const makeTempId = () => `offline-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

async function coalesceUpdateMutation(resourceUrl, entityId, payload) {
  const mutations = await db.getMutations();
  const existing = mutations.find((m) => m.entity === "captures" && m.method === "PATCH" && m.entityId === entityId);
  if (existing) {
    await db.updateMutation({ ...existing, payload: { ...existing.payload, ...payload }, clientTimestamp: Date.now() });
  } else {
    await db.addMutation({ entity: "captures", method: "PATCH", resourceUrl, entityId, payload, clientTimestamp: Date.now() });
  }
}

export async function listCaptures(params) {
  if (navigator.onLine) {
    try {
      const { data } = await api.get("/captures", { params });
      const now = Date.now();
      await db.putMany(STORE, data.map((c) => ({ ...c, cachedAt: now })));
      const pendingIds = new Set((await db.getMutations()).filter((m) => m.entity === "captures").map((m) => m.entityId));
      pruneCache(pendingIds);
      return data;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  let cached = await db.getAll(STORE);
  if (params?.tag_id && params.tag_id !== "__today__") {
    cached = cached.filter((c) => c.tags?.includes(params.tag_id));
  }
  return cached.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
}

function pruneCache(pendingIds) {
  db.pruneCache(STORE, CACHE_DAYS, pendingIds).catch(() => {});
}

export async function getCapture(id) {
  if (navigator.onLine) {
    try {
      const { data } = await api.get(`/captures/${id}`);
      await db.putOne(STORE, { ...data, cachedAt: Date.now() });
      return data;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  return db.getOne(STORE, id);
}

export async function createCapture(payload) {
  if (navigator.onLine) {
    try {
      const { data } = await api.post("/captures", payload);
      await db.putOne(STORE, { ...data, cachedAt: Date.now() });
      return data;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  const id = makeTempId();
  const now = new Date().toISOString();
  const localItem = { id, type: payload.image_data ? "image" : payload.pdf_data ? "pdf" : "text", ...payload, created_at: now, updated_at: now, offlinePending: true, cachedAt: Date.now() };
  await db.putOne(STORE, localItem);
  await db.addMutation({ entity: "captures", method: "POST", collectionUrl: "/captures", tempId: id, entityId: id, payload, clientTimestamp: Date.now() });
  return localItem;
}

export async function updateCapture(id, payload) {
  if (navigator.onLine) {
    try {
      const { data } = await api.patch(`/captures/${id}`, payload);
      await db.putOne(STORE, { ...data, cachedAt: Date.now() });
      return data;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  const existing = (await db.getOne(STORE, id)) || { id };
  const merged = { ...existing, ...payload, updated_at: new Date().toISOString(), offlinePending: true, cachedAt: Date.now() };
  await db.putOne(STORE, merged);
  await coalesceUpdateMutation(`/captures/${id}`, id, payload);
  return merged;
}

export async function deleteCapture(id) {
  if (navigator.onLine) {
    try {
      await api.delete(`/captures/${id}`);
      await db.deleteOne(STORE, id);
      return;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  await db.deleteOne(STORE, id);
  const mutations = await db.getMutations();
  const related = mutations.filter((m) => m.entity === "captures" && m.entityId === id);
  const pendingCreate = related.find((m) => m.method === "POST");
  if (pendingCreate) {
    // Never existed server-side - cancel the create and any queued edits,
    // there is nothing left to delete remotely.
    await Promise.all(related.map((m) => db.removeMutation(m.seq)));
    return;
  }
  await Promise.all(related.filter((m) => m.method === "PATCH").map((m) => db.removeMutation(m.seq)));
  await db.addMutation({ entity: "captures", method: "DELETE", resourceUrl: `/captures/${id}`, entityId: id, clientTimestamp: Date.now() });
}

export function cacheCaptureLocally(capture) {
  return db.putOne(STORE, { ...capture, cachedAt: Date.now() });
}
